import { ALARM_TALK_FLAG } from '#const/alarmtalk/alarmTalk';
import { PATH } from '#const/path';
import { useModalConfirm } from '#hooks/useModalConfirm';
import { KakaoLoginSuccessPage, KakaoLogoutSuccessPage } from '#pages';
import { Dashboard } from '#providers/Dashboard';
import SchedulesManage from '#providers/SchedulesManage/SchedulesManage';
import { useUserInfo } from '#providers/UserInfo';
import MetaHelmet from '#utils/MetaHelmet';
import { setCookie } from '#utils/cookieManager';
import { makeFinalDestination } from '#utils/makeFinalDestination';
import { NativeInterceptor } from '#utils/native-interceptor';
import sessionStorageManager from '#utils/sessionStorageManager';
import { useWNInterface } from '#utils/wni';
import {
  ApplicationView,
  BillingView,
  ChatView,
  DashboardView,
  KakaoLoginErrorView,
  OnBoardView,
  ProfileView,
  ScheduleManageView,
  SignView,
  WelcomeView
} from '#views';
import { useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import ReactGA from 'react-ga4';
import TagManager from 'react-gtm-module';
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useSearchParams
} from 'react-router-dom';
import styled from 'styled-components';
import ModalConfirm from './components/molecules/ModalConfirm';
import { ChartView } from './components/views/ChartView';
import { ClientRegistration } from './shared/components/client-registration/ClientRegistration';
import { HighlightPage } from './components/pages/HighlightPage';
import { ClinicalView } from './components/views/ClinicalView';
import { PortfolioView } from '#views/PortfolioView';
import { LandingPageV2 } from '#pages/LandingPageV2';
import { CommunityView } from '#views/CommunityView-refactor';
import MainPage from 'src/r_page/main';
import TrainerCommunityView from '#views/trainer-community';
import Navigation from 'src/r_page/FooterNavigation';
import { SettingsView } from '#views/SettingsView';
import ManageView from '#views/ManageView';
import TrainerView from '#views/TrainerView';
import Event from './refactor_page/event';

interface RouteWithConditionProps {
  isView: boolean;
}

const TopWrapper = styled.div`
  margin: 0 auto;
  max-width: var(--max-width);
  width: 100%;
`;

const Layout = ({
  useTopWrapper,
  children: children
}: {
  useTopWrapper: boolean;
  children: React.ReactNode;
}) => {
  return useTopWrapper ? <TopWrapper>{children}</TopWrapper> : <>{children}</>;
};

/**
 * 로그인이 필요한 페이지 렌더링 처리
 * 로그인 안되어 있을 경우 로그인 페이지로 이동
 * @returns
 */
const RouteWithCondition = ({ isView }: RouteWithConditionProps) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { setAlarmTalkRedirectPath } = sessionStorageManager();

  useEffect(() => {
    if (isView) return;
    // TokenController.clear();
  }, []);

  if (!isView) {
    const isAlarmTalk = searchParams.get(ALARM_TALK_FLAG);
    if (isAlarmTalk) {
      setAlarmTalkRedirectPath(
        makeFinalDestination(ALARM_TALK_FLAG, searchParams, location.pathname)
      );
    }

    return (
      <Navigate
        replace={true}
        to={PATH.SIGN}
        state={{ blockedPagePath: location.pathname + location.search }}
      />
    );
  }
  return <Outlet />;
};

const App = () => {
  const { isLogIn, userInfo } = useUserInfo();
  const nativeInterceptor = new NativeInterceptor();
  const wni = useWNInterface();
  const { isVisible, closeModalConfirm, ...restModalConfirmState } =
    useModalConfirm();

  useEffect(() => {
    setCookie('state', 'COACH');
  }, []);

  useEffect(() => {
    nativeInterceptor.initialize();
    initWNInterface();
    // ChannelService.loadScript(); // 채널톡   // if (process.env.REACT_APP_NODE_ENV === 'development') return;

    const tagManagerArgs = {
      gtmId: `${process.env.REACT_APP_GTM_ID}`
    };

    ReactGA.initialize(`${process.env.REACT_APP_GA_ID}`);
    TagManager.initialize(tagManagerArgs);
  }, []);

  useEffect(() => {
    // ChannelService.boot({
    //   pluginKey: `${process.env.REACT_APP_CHANNEL_PLUGIN_KEY}`,
    //   memberId: userInfo?.id,
    //   profile: {
    //     name: userInfo?.name,
    //     mobileNumber: userInfo?.phone,
    //     email: userInfo?.email
    //   }
    // });
  }, [userInfo?.id]);

  function initWNInterface() {
    wni.execute('wnWebContentLoaded', {});

    return new Promise(resolve => {
      wni.onReady(() => {
        wni.onAppear(() => {
          onSetBadgeNumber();
        });
        resolve(wni);
      });
    });
  }

  function onSetBadgeNumber(number = 0) {
    wni.execute('wnPushBadgeNumber', {
      number
    });
  }

  const NavigationOutlet = () => {
    return (
      <Navigation>
        <Outlet />
      </Navigation>
    );
  };

  return (
    <>
      <MetaHelmet />
      <BrowserRouter>
        <ErrorBoundary fallback={<div>에러가 발생했습니다.</div>}>
          <Dashboard>
            <SchedulesManage>
              <Routes>
                <Route path="/" element={<LandingPageV2 />} />
                <Route path="/event/1" element={<Event />} />
                <Route
                  path="/loginSuccess"
                  element={
                    <Layout
                      useTopWrapper={true}
                      children={<KakaoLoginSuccessPage />}
                    />
                  }
                />
                <Route
                  path="/sign/*"
                  element={
                    <Layout useTopWrapper={true} children={<SignView />} />
                  }
                />
                <Route element={<NavigationOutlet />}>
                  <Route
                    path="/community/*"
                    element={
                      <Layout
                        useTopWrapper={true}
                        children={<CommunityView />}
                      />
                    }
                  />
                  <Route
                    path="/settings/*"
                    element={
                      <Layout
                        useTopWrapper={true}
                        children={<SettingsView />}
                      />
                    }
                  />
                  <Route
                    path="/trainer-community/*"
                    element={
                      <Layout
                        useTopWrapper={true}
                        children={<TrainerCommunityView />}
                      />
                    }
                  />
                  <Route
                    path="/home"
                    element={
                      <Layout useTopWrapper={true} children={<MainPage />} />
                    }
                  />
                  <Route
                    path="/trainer/*"
                    element={
                      <Layout useTopWrapper={true} children={<TrainerView />} />
                    }
                  />

                  {/* <Route
                  path="/gym-location"
                  element={
                    <Layout
                      useTopWrapper={true}
                      children={<GymLocationPage />}
                    />
                  }
                /> */}
                  <Route
                    path="/welcome/*"
                    element={
                      <Layout useTopWrapper={true} children={<WelcomeView />} />
                    }
                  />
                  <Route
                    path="/auth/leave"
                    element={
                      <Layout
                        useTopWrapper={true}
                        children={<KakaoLogoutSuccessPage />}
                      />
                    }
                  />
                  <Route
                    path="/newSocialAccount"
                    element={
                      <Layout
                        useTopWrapper={true}
                        children={<KakaoLoginSuccessPage />}
                      />
                    }
                  />
                  <Route
                    path="/error/*"
                    element={
                      <Layout
                        useTopWrapper={true}
                        children={<KakaoLoginErrorView />}
                      />
                    }
                  />
                  <Route element={<RouteWithCondition isView={isLogIn} />}>
                    {/* <Route
                      path="/mypage/*"
                      element={
                        <Layout
                          useTopWrapper={true}
                          children={<MyPageView />}
                        />
                      }
                    /> */}
                    <Route
                      path="/manage/*"
                      element={
                        <Layout
                          useTopWrapper={true}
                          children={<ManageView />}
                        />
                      }
                    />
                    <Route
                      path="/chat/*"
                      element={
                        <Layout useTopWrapper={true} children={<ChatView />} />
                      }
                    />
                    <Route
                      path="/application/*"
                      element={
                        <Layout
                          useTopWrapper={true}
                          children={<ApplicationView />}
                        />
                      }
                    />
                    <Route
                      path="/profile/*"
                      element={
                        <Layout
                          useTopWrapper={true}
                          children={<ProfileView />}
                        />
                      }
                    />

                    <Route
                      path="/dashboard/*"
                      element={
                        <Layout
                          useTopWrapper={true}
                          children={<DashboardView />}
                        />
                      }
                    />
                    <Route
                      path="/billing/*"
                      element={
                        <Layout
                          useTopWrapper={true}
                          children={<BillingView />}
                        />
                      }
                    />
                    <Route
                      path="/chart/*"
                      element={
                        <Layout useTopWrapper={true} children={<ChartView />} />
                      }
                    />
                    <Route
                      path="/calender/*"
                      element={
                        <Layout
                          useTopWrapper={true}
                          children={<ScheduleManageView />}
                        />
                      }
                    />
                    <Route
                      path="/onboard/*"
                      element={
                        <Layout
                          useTopWrapper={true}
                          children={<OnBoardView />}
                        />
                      }
                    />
                    <Route
                      path="/client-registration"
                      element={<ClientRegistration />}
                    />
                  </Route>
                  <Route
                    path="/highlight/*"
                    element={
                      <Layout
                        useTopWrapper={true}
                        children={<HighlightPage />}
                      />
                    }
                  />
                  <Route
                    path="/clinical/*"
                    element={
                      <Layout
                        useTopWrapper={true}
                        children={<ClinicalView />}
                      />
                    }
                  />
                  <Route
                    path="/portfolio/*"
                    element={
                      <Layout
                        useTopWrapper={true}
                        children={<PortfolioView />}
                      />
                    }
                  />
                  {/* <Route
                  path="/MyBadge/*"
                  element={
                    <Layout useTopWrapper={true} children={<MyBadgeView />} />
                  }
                /> */}
                </Route>
              </Routes>
            </SchedulesManage>
          </Dashboard>
        </ErrorBoundary>
      </BrowserRouter>

      {isVisible && (
        <ModalConfirm
          {...restModalConfirmState}
          handleClose={() => closeModalConfirm()}
        />
      )}
    </>
  );
};

export default App;
